import {
  ArpMedia,
  ArpMediaType,
  hasComposition,
  KeyAndScale,
  ORDER,
  StemType
} from '@arpeggi/shared'
import { Icons } from '@arpeggi/ui'
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { ArpTable } from '../../../../../features/arp-table'
import { TableRow as TR } from '../../../../../features/arp-tablerow'
import { CurrentPageContext, useVerifyMedia } from '../../../../../features/verified-media'
import { keyframes, styled } from '../../../../lib/stitches'
import { deserializeStemFromStorage } from '../../../../stores/song/stem/StemUtils'
import { Box, IconButton } from '../../../core/Components'
import { FavoriteArpMediaButton } from '../../../core/FavoriteButton'
import {
  ChevronDown,
  ChevronUp,
  DotsVertical,
  RepeatIcon,
  StemIcon as Stem
} from '../../../core/icons'
import { Check, Details, Loop, Share, Soundwave, User } from '../../../core/icons/responsive-icons'
import Skeleton from '../../../core/Skeleton'
import { segmentTrackRemix } from '../../../core/Utils'
import AudioVisualizer from '../../../player/AudioVisualizer'
import { generateNoise } from '../../../player/common/VisualizerWithOverlay'
import NewStemVisualizer from '../../../studio/discover-stems/NewStemVisualizer'
import ProfileAvatar from '../../explore/common/ProfileAvatar'
import { useAsyncEffect, useRemix } from '../../explore/common/utils'
import type { OrderBy } from '../hooks/useSounds'
import * as Dropdown from './Dropdown'
import Spinner from './Spinner'
import RawTag from './Tag'

const DEFAULT_MIN_WIDTH = '160px'
type TableRowProps = {
  media: IArpMedia | ArpMedia | any
  active?: boolean
  onSelect?: (args: { id: number; type: ArpMediaType }) => void
  used?: boolean
} & Omit<ComponentProps<typeof Root>, 'onSelect'>

const appear = keyframes({
  '0%': { opacity: 0, transform: 'translateY(10px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' }
})

const Root = styled('div', {
  userSelect: 'none',
  cursor: 'default',
  height: '64px',
  padding: '0px 16px',
  display: 'flex',
  alignItems: 'center',
  background: 'transparent',
  overflow: 'hidden',
  maxWidth: '100vw',
  '&:hover': {
    background: 'rgba(0,0,0,.02)'
  },
  '&:active': {
    background: 'rgba(0,0,0,.04)'
  },
  transition: 'all 200ms ease',
  variants: {
    active: {
      true: {
        background: 'rgba(0,0,0,.02)'
      }
    }
  }
})

const Column = styled('div', {
  alignItems: 'center',
  fontSize: '14px',
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  color: '$gray09',
  flexShrink: 0,
  variants: {
    hidden: {
      true: {
        display: 'none'
      },
      false: {
        display: 'flex'
      }
    }
  }
})

const MainColumn = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexBasis: DEFAULT_MIN_WIDTH,
  flexGrow: 1,
  flexShrink: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
})

const Title = styled('div', {
  overflow: 'hidden',
  fontSize: '14px',
  lineHeight: '14px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  paddingBottom: '8px'
})

const Clickable = styled('span', {
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  '&:hover': {
    textDecoration: 'underline'
  }
})

const Avatar = styled(ProfileAvatar, {
  width: '32px',
  height: '32px',
  marginRight: '8px',
  flexShrink: 0
})

const StemIcon = styled(Stem, {
  width: '16px',
  height: '16px',
  marginRight: '8px',
  flexShrink: 0
})
const SampleIcon = styled(Soundwave, {
  width: '16px',
  height: '16px',
  marginRight: '8px',
  flexShrink: 0
})
const LoopIcon = styled(Loop, {
  width: '16px',
  height: '16px',
  marginRight: '8px',
  flexShrink: 0
})
const SongIcon = () => null

const segmentTableEntry = (media) => {
  window.analytics.track('Button Clicked', {
    button: 'table entry',
    sound: media.metadata.title,
    source: window.location.pathname,
    location: 'explore table',
    artistName: media.artist,
    artistEthAddress: media.artistEthAddress,
    genres: media.metadata.genres,
    bpm: media.metadata.bpm,
    tags: media.metadata.tags
  })
}

const segmentDetails = (media, button) => {
  window.analytics.track('Button Clicked', {
    button: button,
    sound: media.metadata.title,
    source: window.location.pathname,
    location: 'explore table',
    artistName: media.artist,
    artistEthAddress: media.artistEthAddress,
    genres: media.metadata.genres,
    bpm: media.metadata.bpm,
    tags: media.metadata.tags
  })
}

const segmentSort = (button) => {
  window.analytics.track('Button Clicked', {
    button: 'Sort ' + button,
    source: window.location.pathname,
    location: 'explore table'
  })
}

const MediaIcon: FC<ComponentProps<typeof StemIcon> & { type: ArpMediaType }> = ({
  type,
  ...props
}) => {
  if (type === ArpMediaType.SONG) return null
  if (type === ArpMediaType.SAMPLE) return <SampleIcon {...props} />
  if (type === ArpMediaType.STEM) return <RepeatIcon style={{ marginRight: '5px' }} {...props} />
}

const Tag = styled(RawTag, {
  marginRight: '4px'
})

export const IconWrapper = styled('div', {
  width: '16px',
  height: '16px',
  marginRight: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0
})

type DetailsButtonProps = ComponentProps<typeof IconButton> & { media: IArpMedia | ArpMedia }
export const DetailsButton: FC<DetailsButtonProps> = ({ media, children, ...props }) => {
  const currentPageContext = useContext(CurrentPageContext)
  const { verifiedIDs, isVerifiable, onVerify } = useVerifyMedia(currentPageContext)
  const isVerified = verifiedIDs.includes(media.id)

  const navigate = useNavigate()
  const handleArtist = () => {
    segmentDetails(media, 'view artist profile')
    navigate(`/profile/${media.user.walletAddress}`)
  }
  const handleDetails = () => {
    segmentDetails(media, 'open stem details')
    navigate(`/${media.type}?token=${media.id}`)
  }
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    setTimeout(() => setCopied(false), 3000)
  }, [copied])
  const handleShare = (evt) => {
    evt.preventDefault()
    segmentDetails(media, 'share')
    navigator.clipboard.writeText(`${location.origin}/${media.type}?token=${media.id}`)
    setCopied(true)
  }
  const { remixSong, remixStem, remixSample, status } = useRemix()
  const handleRemix = (evt) => {
    evt.preventDefault()
    segmentTrackRemix(media.type, media.id, 'table overflow menu')
    if (media.type === ArpMediaType.SONG) remixSong(media.id)
    if (media.type === ArpMediaType.SAMPLE) remixSample(media.id)
    if (media.type === ArpMediaType.STEM) remixStem(media.id)
  }
  return (
    <Dropdown.Root>
      <DropdownPrimitive.Trigger asChild>
        {children || (
          <IconButton {...props}>
            <DotsVertical />
          </IconButton>
        )}
      </DropdownPrimitive.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content side="bottom" sideOffset={4}>
          <Dropdown.Item onSelect={handleDetails} onClick={(evt) => evt.stopPropagation()}>
            <IconWrapper>
              <Details />
            </IconWrapper>
            {`Open ${media.type} details`}
          </Dropdown.Item>
          <Dropdown.Item onSelect={handleArtist} onClick={(evt) => evt.stopPropagation()}>
            <IconWrapper>
              <User />
            </IconWrapper>
            View artist profile
          </Dropdown.Item>
          <Dropdown.Item onSelect={handleShare} onClick={(evt) => evt.stopPropagation()}>
            <IconWrapper>{copied ? <Check /> : <Share />}</IconWrapper>
            {copied ? 'Copied to clipboard' : 'Share'}
          </Dropdown.Item>
          <Dropdown.Item onSelect={handleRemix} onClick={(evt) => evt.stopPropagation()}>
            <IconWrapper>
              {status === 'loading' ? (
                <Spinner css={{ width: '16px', height: '16px', strokeWidth: '1px' }} />
              ) : (
                <Icons.Studio />
              )}
            </IconWrapper>
            {status === 'loading' ? 'Opening' : 'Open in Studio'}
          </Dropdown.Item>
          {isVerifiable && (
            <Dropdown.Item
              onSelect={() => onVerify(media.id)}
              onClick={(evt) => evt.stopPropagation()}
            >
              <IconWrapper>
                <Icons.Verified />
              </IconWrapper>
              {isVerified ? 'Unverify' : 'Verify'}
            </Dropdown.Item>
          )}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  )
}

const Favorite: FC<{ media: IArpMedia }> = ({ media }) => (
  <FavoriteArpMediaButton media={media} css={{ color: '#F6658E', margin: 0 }} location="table" />
)

const SoundVisualizer: FC<{ media: IArpMedia }> = ({ media }) => {
  const [audioArray, setAudioArray] = useState<Float32Array>()
  const fakeArray = useMemo(
    () => generateNoise(media.id.toString(), 1000, 10),
    [media.id.toString()]
  )
  const status = useAsyncEffect(async () => {
    const buffer = await media.fetchAudioBuffer()
    setAudioArray(buffer.getChannelData(0))
  }, [media])
  return (
    <AudioVisualizer
      audioArray={audioArray || fakeArray}
      options={{ resolution: 2, segmentWidth: 1.5, segmentSpacing: 2, color: '#000000' }}
    />
  )
}

const VisualizerContainer = styled('div', {
  height: '32px',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  opacity: 0.3
})

const Visualizer: FC<{ media: IArpMedia }> = observer(({ media }) => {
  if (
    media.type === ArpMediaType.STEM &&
    hasComposition(media.asInternalArpMedia()) &&
    !isAudioStem(media?.metadata.arpeggiComposition)
  ) {
    const stem = ArpRegistry.retrieveFromCache(media.id)
    if (!stem) return null
    return <NewStemVisualizer stem={stem} css={{ width: '100%' }} />
  }
  return (
    <VisualizerContainer>
      <SoundVisualizer media={media} />
    </VisualizerContainer>
  )
})

const Row = {
  Root,
  MainColumn,
  Column,
  Title,
  Avatar,
  MediaIcon,
  Tag,
  DetailsButton,
  Favorite,
  Visualizer
}

const isAudioStem = (arpeggiComposition: string) => {
  return deserializeStemFromStorage(arpeggiComposition)?.type() === StemType.AUDIO
}

export const BabyTableRow: FC<TableRowProps> = observer(
  ({ media, onSelect, active, used, ...props }) => {
    const { title, tags, key, genres } = media.metadata
    const { type, createdAt, remixCount, favoriteCount } = media
    const navigate = useNavigate()

    const filteredTags = tags.filter((e) => !e.includes(':'))
    const keyString = KeyAndScale.from(key).displayName()

    const handleDetails = () => {
      segmentDetails(media, 'open stem details')
      navigate(media.detailPageUrl)
    }

    const handleClick = (evt) => {
      evt.stopPropagation()
      segmentTableEntry(media)
      onSelect && onSelect({ id: media.id, type: media.type })
    }

    return (
      <Row.Root onClick={handleClick} active={active} css={{ fontFamily: 'DM Sans' }} {...props}>
        <Row.MainColumn>
          <Row.Avatar eth={media.user?.walletAddress} />
          <Row.MediaIcon type={type} />
          <Box css={{ flex: 1 }}>
            <Row.Title css={{ fontWeight: '400' }}>
              <Clickable onClick={handleDetails}>{title}</Clickable>
            </Row.Title>
            <Box css={{ display: 'flex', overflow: 'hidden' }}>
              {filteredTags.map((e, i) => (
                <Row.Tag key={i}>{e}</Row.Tag>
              ))}
              {genres.map((e, i) => (
                <Row.Tag key={i}>{e}</Row.Tag>
              ))}
              <Row.Tag>{keyString}</Row.Tag>
            </Box>
          </Box>
        </Row.MainColumn>
        <Row.Column>
          <Row.DetailsButton media={media} css={{ marginLeft: '8px' }} />
        </Row.Column>
      </Row.Root>
    )
  }
)

export const TableRowSkeleton: FC = () => (
  <Row.Root>
    <Row.MainColumn>
      <Skeleton
        css={{ width: '32px', height: '32px', borderRadius: '9999px', marginRight: '8px' }}
      />
      <Skeleton css={{ width: '16px', height: '16px', marginRight: '8px' }} />
      <Box css={{ flex: 1 }}>
        <Skeleton css={{ width: '100%', maxWidth: '220px', height: '14px', marginBottom: '8px' }} />
        <Box css={{ display: 'flex' }}>
          <Skeleton
            css={{ borderRadius: '9999px', width: '40px', height: '18px', marginRight: '4px' }}
          />
          <Skeleton
            css={{ borderRadius: '9999px', width: '40px', height: '18px', marginRight: '4px' }}
          />
          <Skeleton
            css={{ borderRadius: '9999px', width: '40px', height: '18px', marginRight: '4px' }}
          />
        </Box>
      </Box>
    </Row.MainColumn>
    <Row.Column hidden={{ '@initial': true, '@mobile': false }} css={{ flex: 2 }}>
      <Skeleton css={{ flex: 1, margin: '0px 32px', height: '32px' }} />
    </Row.Column>
    <Row.Column hidden={{ '@initial': true, '@mobile': false }} css={{ marginRight: '32px' }}>
      <Skeleton css={{ width: '40px', height: '10px' }} />
    </Row.Column>
    <Row.Column hidden={{ '@initial': true, '@mobile': false }} css={{ marginRight: '32px' }}>
      <Skeleton css={{ width: '40px', height: '10px' }} />
    </Row.Column>
    <Row.Column hidden={{ '@initial': true, '@mobile': false }} css={{ marginRight: '32px' }}>
      <Skeleton css={{ width: '40px', height: '10px' }} />
    </Row.Column>
    <Row.Column hidden={{ '@initial': true, '@mobile': false }}>
      <Skeleton
        css={{ width: '70px', height: '24px', borderRadius: '9999px', marginRight: '16px' }}
      />
      <Skeleton css={{ width: '16px', height: '16px', borderRadius: '9999px' }} />
    </Row.Column>
  </Row.Root>
)

const TableRow: FC<TableRowProps> = ({ media, onSelect, active, used, ...props }) => {
  const currentPageContext = useContext(CurrentPageContext)

  const handleClick = (evt) => {
    evt.stopPropagation()
    segmentTableEntry(media)
    onSelect && onSelect({ id: media.id, type: media.type })
  }

  return (
    <TR.Root media={media} onClick={handleClick} active={active}>
      <TR.Main />
      <TR.Tags />
      <TR.DateMinted />
      {used && <TR.Remixes />}
      <TR.Favorite />
      <TR.Actions />
    </TR.Root>
  )
}

const SelectableButton = styled('button', {
  all: 'unset',
  background: 'transparent',
  transition: 'all 100ms ease',
  display: 'flex',
  fontSize: '12px',
  lineHeight: '20px',
  alignItems: 'center',
  padding: '0px 4px',
  color: '$gray07',
  borderRadius: '1px',
  '&:hover': {
    background: 'rgba(0,0,0,0.01)',
    color: '$gray15',
    transform: 'translateY(-1px)'
  },
  '&:active': {
    background: 'rgba(0,0,0,0.02)',
    transform: 'translateY(1px)'
  },
  variants: {
    active: {
      true: {
        color: '$gray15'
      }
    }
  }
})

interface TableRowHeaderProps {
  order: ORDER
  orderBy: OrderBy
  onSelect?: (order: OrderBy) => void
  used?: boolean
}

const DownIcon = styled(ChevronDown, { width: '12px', height: '12px' })
const UpIcon = styled(ChevronUp, { width: '12px', height: '12px' })

const { Primitives } = ArpTable
const { Headers } = Primitives

const Icon: FC<{ order: ORDER }> = ({ order }) => (
  <Headers.Icon as={order === 'desc' ? ChevronDown : ChevronUp} />
)

export const TableRowHeader: FC<TableRowHeaderProps> = ({ order, orderBy, onSelect, used }) => {
  const handleSelect = (_orderBy: OrderBy) => () => {
    segmentSort(_orderBy)
    onSelect(_orderBy)
  }
  return (
    <Headers.Root>
      <Headers.Column layout={'main'} clickable onClick={handleSelect('title')}>
        TITLE
        {orderBy === 'title' && <Icon order={order} />}
      </Headers.Column>
      <Headers.Column layout={'fluid2'}>
        DATA
        {orderBy === 'title' && <Icon order={order} />}
      </Headers.Column>
      <Headers.Column layout={'fluid1'} clickable onClick={handleSelect('favoriteCount')}>
        LIKES
        {orderBy === 'favoriteCount' && <Icon order={order} />}
      </Headers.Column>
      <Headers.Column layout={'fluid1'} clickable onClick={handleSelect('remixCount')}>
        REMIXES
        {orderBy === 'remixCount' && <Icon order={order} />}
      </Headers.Column>
    </Headers.Root>
  )
}

export default observer(TableRow)
